'use client';

import { usePathname } from 'next/navigation';
import { ReactNode, Suspense, useRef } from 'react';
import SidebarRail from '@/components/layout/SidebarRail';
import Topbar from '@/components/layout/Topbar';
import MobileDrawer from '@/components/layout/MobileDrawer';
import BottomAppNav from '@/components/layout/BottomAppNav';
import AnnouncementBanner from '@/components/layout/AnnouncementBanner';
import VerifyEmailBanner from '@/components/VerifyEmailBanner';
import SuspendedBanner from '@/components/SuspendedBanner';
import Footer from '@/components/layout/Footer';
import CommandPalette from '@/components/CommandPalette';
import InstallPrompt from '@/components/InstallPrompt';

/**
 * Wraps every route. The /auth/* and /admin/* paths render full-bleed without
 * the sidebar + topbar chrome: auth pages are a design call (full-bleed login
 * panel), admin pages are a separation-of-concerns call (the admin console
 * has its OWN chrome and must not leak the user sidebar — including the
 * CommandPalette that reads `useAuth()`).
 */
export default function AppShell({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const fullBleed = pathname.startsWith('/auth') || pathname.startsWith('/admin');
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  if (fullBleed) return <>{children}</>;

  return (
    <>
      {/* Skip-to-content link — visible only on keyboard focus (Tab from top). */}
      <a href="#main-content" className="skip-link">Skip to content</a>

      <div className="app">
        <SidebarRail />
        <div className="main-col">
          {/* Topbar reads `useSearchParams()` to seed its search box from
              the URL. Next.js 16 requires that hook be wrapped in a
              Suspense boundary so static prerender doesn't bail. */}
          <Suspense fallback={<div style={{ height: 56 }} />}>
            <Topbar hamburgerRef={hamburgerRef} />
          </Suspense>
          {/* Admin-controlled announcement banners — auto-hidden when none
              are active or all have been dismissed by this client. */}
          <AnnouncementBanner />
          <SuspendedBanner />
          <VerifyEmailBanner />
          {/* `<main>` gives screen readers + search engines a clear "start of
              page content" anchor. One per page is the semantic convention. */}
          <main id="main-content">{children}</main>
          <Footer />
        </div>
        {/* Global ⌘K command palette — listens for `cmdpalette:open` event. */}
        <CommandPalette />
        <BottomAppNav />
      </div>

      {/* Mobile slide-in nav drawer — portals to <body>, mobile only. */}
      <MobileDrawer triggerRef={hamburgerRef} />

      {/* PWA install prompt — shown only when browser fires beforeinstallprompt. */}
      <InstallPrompt />
    </>
  );
}
